import { create } from "zustand";
import { devtools } from "zustand/middleware";
import authService from "@/features/auth/services/authService";
import logger from "@/logger";

/**
 * Authentication state for the web dashboard
 */
interface AuthState {
    /** Currently authenticated user */
    uid: string | null;
    /** Single status that captures the full auth lifecycle */
    status: "pending" | "authenticated" | "unauthenticated" | "error";
    /** Authentication error message */
    error: string | null;
}

/**
 * Authentication actions
 */
interface AuthActions {
    /** Set authentication status */
    setStatus: (status: AuthState["status"]) => void;
    /** Set authenticated user */
    setUser: (uid: string) => void;
    /** Set authentication error */
    setError: (error: string | null) => void;
    /** Clear all authentication state */
    clearAuth: () => void;
}

/**
 * Combined authentication store type
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Initial authentication state
 */
const initialState: AuthState = {
    error: null,
    status: "pending", // Start pending, not loading
    uid: null,
};

/**
 * Zustand store for authentication state management
 *
 * Handles user authentication state and auth status.
 * Integrates with Auth Service for actual authentication.
 * Automatically initialises authentication state on creation.
 */
export const useAuthStore = create<AuthStore>()(
    devtools(
        (set) => {
            // Initialise authentication state
            authService.onAuthStateChanged(async (user) => {
                try {
                    // Check if user has admin access (adminAccessGrantedAt is defined)
                    if (user?.adminAccessGrantedAt) {
                        set(
                            {
                                error: null,
                                status: !user.uid ? "unauthenticated" : "authenticated",
                                uid: user.uid,
                            },
                            false,
                            "auth/setUser"
                        );
                    } else {
                        // User is signed out or not admin, clear auth state
                        set(
                            {
                                error: null,
                                status: "unauthenticated",
                                uid: null,
                            },
                            false,
                            "auth/clearAuth"
                        );
                    }
                } catch (error) {
                    logger.error("Failed to initialise auth:", error);
                    set(
                        {
                            error: "Failed to initialise authentication",
                            status: "error",
                        },
                        false,
                        "auth/setError"
                    );
                }
            });

            return {
                ...initialState,

                clearAuth: () => set(() => initialState, false, "auth/clearAuth"),

                setError: (error) => set((state) => ({ ...state, error, status: "error" }), false, "auth/setError"),

                setStatus: (status) => set((state) => ({ ...state, status }), false, "auth/setStatus"),

                setUser: (uid) =>
                    set(
                        (state) => ({
                            ...state,
                            error: null,
                            status: "authenticated",
                            uid,
                        }),
                        false,
                        "auth/setUser"
                    ),
            };
        },
        {
            enabled: import.meta.env.DEV,
            name: "book-club-auth-store",
        }
    )
);
